import React, { useMemo, useRef, useEffect, useState } from 'react';
import { Button, Flex, Table, FilterBuilder, FieldType } from 'onefaceui-react-components';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'react/components/Modal';
import defaultFetch from 'react/api/sagas/fetch/defaultFetch';
import { useMainSupervisor } from 'react/queries/MainSupervisor';
import { actionCreators as actionsModals } from 'react/api/actions/modals';
import AlertGenericModal from 'react/containers/modals/AlertGenericModal';
import { getResourceTo } from 'react/utils/utils';
import { Container, ModalFooter } from './styles';

export default function MainSupervisorModal() {
  const dispatch = useDispatch();
  const MODAL_KEY = 'MainSupervisorModal';
  const { title, isOpen, data } = useSelector((state) => state.modals.get(MODAL_KEY).toJS());
  const currentUserData = Array.isArray(data) ? data : [data];

  const [defaultData, setDefaultData] = useState([]);
  const [isFetching, setIsFetching] = useState(true);
  const [driverIds, setDriverIds] = useState([]);
  const [selectedDriverIds, setSelectedDriverIds] = useState([]);
  const [confirmChangeSupervisor, setConfirmChangeSupervisor] = useState(false);

  const tableRef = useRef(null);
  const filters = useRef({});

  const { data: fetchedDrivers, isLoading: isLoadingDrivers, refetch } = useMainSupervisor(driverIds.length > 0 ? driverIds : null);

  useEffect(() => {
    if (!isOpen) {
      setDriverIds([]);
      setDefaultData([]);
      setIsFetching(true);
      return;
    }

    setIsFetching(true);
    const ids = (data?.drivers || []).map(item => item.driver?.id).filter(Boolean);
    setDriverIds(prevIds => (JSON.stringify(prevIds) !== JSON.stringify(ids) ? ids : prevIds));
    if (ids.length === 0) setIsFetching(false);
  }, [isOpen]);

  const mapDrivers = (currentUserData, fetchedDrivers) => {
    const driverMap = new Map((fetchedDrivers ?? []).map(d => [d.id, d]));

    return currentUserData.flatMap(driver =>
      (driver.drivers || []).map(({ driver: driverInfo }) => {
        if (!driverInfo) return null;
        const driverDetails = driverMap.get(driverInfo.id) || {};

        return {
          checkbox: false,
          id: driverInfo.id,
          key: driverDetails.key || "",
          login: driverDetails.login || "",
          name: driverInfo.name,
          mainSupervisor: driverDetails.mainSupervisor?.name || "",
        };
      }).filter(Boolean)
    );
  };

  useEffect(() => {
    if (!isOpen) return;

    if (isLoadingDrivers) {
      setIsFetching(true);
      return;
    }

    if (!Array.isArray(fetchedDrivers)) {
      setIsFetching(false);
      return;
    }

    const allDrivers = mapDrivers(currentUserData, fetchedDrivers);

    setDefaultData(allDrivers);
    setIsFetching(!(allDrivers.length > 0 || driverIds.length === 0));

  }, [fetchedDrivers, isLoadingDrivers]);


  const defineMainSupervisor = async (drivers) => {
    setIsFetching(true);
    await defaultFetch('/Driver/AssignMainSupervisor', {
      driversIds: drivers.map(item => item.id),
      mainSupervisorId: currentUserData[0]?.userId
    }, 'POST');
    await refetch();
    toggleSupervisorConfirmationModal();
    setIsFetching(false);
  };

  const toggleSupervisorConfirmationModal = () => {
    setConfirmChangeSupervisor(prev => !prev);
  };

  const handleSupervisorSelection = () => {
    setDriverIds((prevDriverIds) => {
      setConfirmChangeSupervisor(!confirmChangeSupervisor);
      return prevDriverIds;
    });
  };

  const closeModal = () => {
    dispatch(actionsModals.closeModal({ modalKey: MODAL_KEY }));
  };

  const getContainer = () => {
    let container = document.querySelector(
      '.mainContainer .reactivo-tabs.gmTabs #mainSupervisorModalWrappers'
    );

    if (!container) {
      container = document.createElement('div');
      container.id = 'mainSupervisorModalWrappers';

      document
        .querySelector('.mainContainer .reactivo-tabs.gmTabs')
        .appendChild(container);
    }

    return container;
  };

  const columns = useMemo(() => [
    { title: getResourceTo('TextFile.user.key'), field: 'key', hozAlign: 'center' },
    { title: getResourceTo('TextFile.user.name'), field: 'name', hozAlign: 'center' },
    { title: getResourceTo('TextFile.user.login'), field: 'login', hozAlign: 'center' },
    { title: getResourceTo('TextFile.user.mainSupervisor'), field: 'mainSupervisor', hozAlign: 'center' }
  ], []);

  const filterBuilderFields = useMemo(() => [
    { id: 'key', label: getResourceTo('TextFile.user.key'), name: 'key', type: FieldType.TEXT },
    { id: 'name', label: getResourceTo('TextFile.user.name'), name: 'name', type: FieldType.TEXT },
    { id: 'login', label: getResourceTo('TextFile.user.login'), name: 'login', type: FieldType.TEXT },
    { id: 'mainSupervisor', label: getResourceTo('TextFile.user.mainSupervisor'), name: 'mainSupervisor', type: FieldType.TEXT }
  ], []);

  const handleApplyFilters = async (values) => {
    tableRef.current?.setLoading?.(true);
    tableRef.current?.resetPagination?.();
    tableRef.current?.clearData?.();

    const payload = getParsedPayload(values);
    filters.current = payload;

    try {
      const filteredData = (defaultData ?? []).filter(item =>
        Object.entries(payload).every(([key, value]) =>
          String(item[key] ?? "").toLowerCase().includes(String(value).toLowerCase())
        )
      );

      tableRef.current?.setData(filteredData);
    } catch (error) {
      console.error(error);
    } finally {
      tableRef.current?.setLoading?.(false);
    }
  };

  const getParsedPayload = (values) => {
    const payload = {};

    for (const key in values) {
      const value = values[key];
      if ([undefined, ''].includes(value)) {
        continue;
      }
      payload[key] = value;
    }

    return payload;
  };

  const ModalFooterContainer = (
    <ModalFooter justifyContent="flex-end" gap={12}>
      <Button
        dataTestId="close-modal-button"
        variant="secondary"
        onClick={closeModal}
      >
        {getResourceTo('default.button.close')}
      </Button>
    </ModalFooter>
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        closeModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, closeModal]);


  return (
    <Modal
      className="mainSupervisorModal"
      id="mainSupervisorModalWrappers"
      isOpen={isOpen}
      title={getResourceTo(title)}
      subtitle={getResourceTo('ManageDriverSupervisor.Subtitle')}
      crossAxisBtn={false}
      closeModalFn={closeModal}
      parentSelector={() => getContainer()}
      footer={ModalFooterContainer}
      shouldCloseOnOverlayClick
      onRequestClose={closeModal}
    >
      <Container data-testid="mainSupervisorModal-container">
        <div className="monetInlineAlerContainer" />
        <Flex>
          <FilterBuilder
            onSubmit={handleApplyFilters}
            fields={filterBuilderFields}
            defaultSelected={"name"}
          />
        </Flex>
        <Flex>
          <Table
            isLoading={isFetching && defaultData}
            headerOptions={{
              showResults: true,
              actions: {
                userInfo(selectedIds) {
                  if (selectedIds.length) {
                    setSelectedDriverIds(selectedIds);
                    handleSupervisorSelection();
                  }
                }
              }
            }}
            columns={columns}
            defaultData={defaultData}
            ref={tableRef}
            columnsPreference={[
              { field: 'key' },
              { field: 'name' },
              { field: 'login' },
              { field: 'mainSupervisor' }
            ]}
          />
        </Flex>
      </Container>

      <AlertGenericModal
        open={confirmChangeSupervisor}
        i18n={Monet.I18n}
        title={getResourceTo("ModalMainSupervisor.Title")}
        message={getResourceTo("ModalMainSupervisor.Content")}
        isLoading={isFetching}
        confirmClickButton={() => defineMainSupervisor(selectedDriverIds)}
        cancelClickButton={handleSupervisorSelection}
      />
    </Modal>

  );
}
